/**
 * Aion-style cast bar for the target frame, party frames and nameplates: spell icon on the left,
 * a thin bar with the spell name and time left. Interruptible casts glow gold-white; locked
 * (uninterruptible) casts are grey with a shield mark. Channels drain.
 * +castux: an interruptible enemy cast turns the kick colour while the player's interrupt is
 * ready (castCues.kickReady), and an interrupted cast holds the bar for a moment with
 * "Interrupted by <name>" (castCues interrupts, party kicks included).
 *
 * Per frame: the fill is an interned `scaleX()` (no layout), the time label is an interned
 * timer string, and the name/target label is rebuilt only when the cast changes.
 */
import type { ContentRegistry, DeepReadonly, Unit, World } from '@keystone/sim';
import { hudNow, shownCast } from '../../game/prediction';
import { SCHOOL_COLOR } from '../../render/palette';
import { el } from '../dom';
import { initials } from '../format';
import { iconUrl } from '../icons';
import { SCALE_X, barIndex, cooldownKey, timerLabel } from '../kit';
import { type TooltipLive, castTooltipModel } from '../tooltip/hud';
import { attachAbilityTooltip } from '../tooltip/view';
import { castWarningFor } from '../../render/toon/fx/mechanics/warnings';
import { castCues, interruptShown, interruptedLabel } from '../castCues'; // +castux

export interface FrameCastBarOptions {
  /** Extra classes on the root ('ft-cast', 'np-cast', …). */
  className?: string;
  /** Show the spell icon (default true). */
  icon?: boolean;
  /** Append "→ Target" to the spell name (default true). */
  showTarget?: boolean;
}

export class FrameCastBar {
  readonly root: HTMLElement;
  private readonly iconBox: HTMLElement | null;
  private readonly img: HTMLImageElement | null;
  private readonly fallback: HTMLElement | null;
  private readonly fill: HTMLElement;
  private readonly label: HTMLElement;
  private readonly time: HTMLElement;
  /** Counterplay tag for warned enemy casts ("SOAK", "KICK"…), styles in bossWarning.css. */
  private readonly warn: HTMLElement;
  private warned = '';
  private readonly showTarget: boolean;
  private active = false;
  private locked = false;
  private channel = false;
  private fillIdx = -1;
  private timeKey = -1;
  private abilityId: string | null = null;
  private targetId: string | null = null;
  /** +castux: kick-colour cue and the interrupted hold. */
  private kickable = false;
  private interruptedAt = -1;
  /** Mutable tooltip context (no per-frame closure). */
  private readonly live: TooltipLive & { unitId: string | null } = { world: null as never, content: null as never, playerId: null, unitId: null };

  constructor(parent: HTMLElement | null, opts: FrameCastBarOptions = {}) {
    this.root = el('div', `fc-cast${opts.className ? ` ${opts.className}` : ''}`);
    this.showTarget = opts.showTarget !== false;
    if (opts.icon !== false) {
      this.iconBox = el('div', 'fc-cast-icon', this.root);
      this.img = el('img', 'fc-cast-img', this.iconBox);
      this.img.alt = '';
      this.img.draggable = false;
      this.img.decoding = 'async';
      this.fallback = el('div', 'fc-cast-fallback', this.iconBox);
    } else {
      this.iconBox = this.img = this.fallback = null;
    }
    const track = el('div', 'fc-cast-track', this.root);
    this.fill = el('div', 'fc-cast-fill', track);
    el('div', 'fc-cast-spark', track);
    this.label = el('div', 'fc-cast-label', track);
    this.time = el('div', 'fc-cast-time hk-num', track);
    this.warn = el('div', 'fc-cast-warn', this.root);
    attachAbilityTooltip(this.root, () => (this.live.world && this.live.unitId ? castTooltipModel(this.live, this.live.unitId) : null));
    parent?.appendChild(this.root);
  }

  private setWarning(u: DeepReadonly<Unit>, content: ContentRegistry, abilityId: string): void {
    const w = u.faction === 'party' ? null : castWarningFor(content, abilityId, u.tier === 'boss');
    const key = w?.hint ? `${w.severity}:${w.hint}` : '';
    if (key === this.warned) return;
    this.warned = key;
    this.root.classList.toggle('warned', !!key);
    this.root.classList.toggle('warn-deadly', w?.severity === 'deadly');
    if (w?.hint) {
      this.warn.textContent = w.hint;
      this.root.style.setProperty('--fc-warn', w.color);
    }
  }

  /** True while a cast is shown. */
  get casting(): boolean {
    return this.active;
  }

  update(u: DeepReadonly<Unit> | undefined, world: DeepReadonly<World>, content: ContentRegistry): void {
    this.live.world = world;
    this.live.content = content;
    this.live.unitId = u?.id ?? null;
    const c = u?.alive ? shownCast(world, u.id) : null;
    if (!!c !== this.active) {
      this.active = !!c;
      this.root.classList.toggle('active', this.active);
    }
    // +castux: kick colour (interruptible enemy cast, the player's interrupt ready).
    const kickable = !!c && c.interruptible && castCues.kickReady && u!.faction !== 'party';
    if (kickable !== this.kickable) {
      this.kickable = kickable;
      this.root.classList.toggle('kickable', kickable);
    }
    // +castux: interrupted: hold the bar with "Interrupted by <name>" for a moment.
    const note = !c && u && castCues.interrupts.size > 0 ? interruptShown(u.id, typeof performance !== 'undefined' ? performance.now() : 0) : null;
    const at = note ? note.at : -1;
    if (at !== this.interruptedAt) {
      this.interruptedAt = at;
      this.root.classList.toggle('interrupted', !!note);
      if (note) {
        this.label.textContent = interruptedLabel(note.by || null);
        this.time.textContent = '';
        this.fillIdx = -1;
        this.fill.style.transform = SCALE_X[SCALE_X.length - 1]!;
        // The next cast rewrites its label / time.
        this.abilityId = null;
        this.timeKey = -1;
      }
    }
    if (!c) return;
    const locked = !c.interruptible;
    if (locked !== this.locked) {
      this.locked = locked;
      this.root.classList.toggle('locked', locked);
    }
    const channel = c.kind === 'channel';
    if (channel !== this.channel) {
      this.channel = channel;
      this.root.classList.toggle('channel', channel);
    }
    // Sub-tick clock: smooth every frame, not in 50 ms steps.
    const now = hudNow(world);
    const total = Math.max(1, c.endsAt - c.startedAt);
    const done = Math.min(1, Math.max(0, (now - c.startedAt) / total));
    const fi = barIndex(channel ? 1 - done : done);
    if (fi !== this.fillIdx) {
      this.fillIdx = fi;
      this.fill.style.transform = SCALE_X[fi]!;
    }
    const tk = cooldownKey(c.endsAt - now);
    if (tk !== this.timeKey) {
      this.timeKey = tk;
      this.time.textContent = timerLabel(tk);
    }
    const tgt = this.showTarget && c.targetId && c.targetId !== u!.id ? c.targetId : null;
    if (c.abilityId !== this.abilityId || tgt !== this.targetId) {
      const newSpell = c.abilityId !== this.abilityId;
      this.abilityId = c.abilityId;
      this.targetId = tgt;
      const ab = content.ability(c.abilityId);
      const name = ab?.name ?? c.abilityId;
      const tname = tgt ? world.units[tgt]?.name : undefined;
      this.label.textContent = tname ? `${name} › ${tname}` : name;
      if (newSpell) this.setWarning(u!, content, c.abilityId);
      if (newSpell && this.img && this.fallback) {
        const url = iconUrl(c.abilityId, 64);
        if (url) {
          this.img.src = url;
          this.img.hidden = false;
          this.fallback.hidden = true;
        } else {
          this.img.removeAttribute('src');
          this.img.hidden = true;
          this.fallback.hidden = false;
          this.fallback.textContent = initials(name);
          this.fallback.style.setProperty('--school', ab?.ui?.color ?? SCHOOL_COLOR[ab?.school ?? 'physical'] ?? '#888');
        }
      }
    }
  }
}
